import type { SpheresBackground } from './animation';
import type { Project } from './projects';

/* Spheres tinted to the paper background: they read as faint embossed
   contours rather than objects. Created on open, disposed on close. */
const OVERLAY_PALETTE = { bg: '#f1efea', c0: '#dbd5c8', c1: '#e9e5dc' };

let overlayFx: SpheresBackground | null = null;

async function startOverlayFx(overlay: HTMLElement): Promise<void> {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(pointer: fine)').matches;
  if (reducedMotion || !finePointer || overlayFx) return;
  const { loadAnimation } = await import('./animation');
  overlayFx = loadAnimation(overlay, {
    palette: OVERLAY_PALETTE,
    zIndex: '0',
    maxOpacity: 0.15,
  });
}

function stopOverlayFx(): void {
  overlayFx?.dispose();
  overlayFx = null;
}

/**
 * Full-screen project detail overlay. Opens in place (no route reload),
 * traps focus, closes on Escape or the Close button, and returns focus
 * to the card that opened it.
 */
export function initOverlay(projects: Project[]): (slug: string, trigger?: HTMLElement | null) => void {
  const overlay = document.getElementById('project-overlay')!;
  const closeBtn = document.getElementById('overlay-close')!;
  const title = document.getElementById('overlay-title')!;
  const spec = (name: string) => overlay.querySelector<HTMLElement>(`[data-spec="${name}"]`)!;

  let lastTrigger: HTMLElement | null = null;

  function close(): void {
    overlay.hidden = true;
    stopOverlayFx();
    document.body.classList.remove('overlay-open');
    document.removeEventListener('keydown', onKeydown);
    lastTrigger?.focus();
    lastTrigger = null;
    if (location.hash.startsWith('#work/')) {
      history.replaceState(null, '', location.pathname + location.search);
    }
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    // Focus trap
    const focusables = overlay.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  closeBtn.addEventListener('click', close);

  return function open(slug: string, trigger?: HTMLElement | null): void {
    const p = projects.find(pr => pr.slug === slug);
    if (!p) return;
    lastTrigger = trigger ?? null;
    history.replaceState(null, '', `#work/${slug}`);

    title.textContent = p.title;
    spec('date').textContent = p.date;
    spec('distinctions').textContent = p.distinctions;
    spec('with').textContent = p.with_;
    spec('role').textContent = p.role;
    spec('description').textContent = p.description;
    const link = spec('link') as HTMLAnchorElement;
    const hasUrl = Boolean(p.url) && p.url !== '#';
    link.href = hasUrl ? p.url : '#';
    link.parentElement!.hidden = !hasUrl;

    const media = spec('media');
    media.innerHTML = '';

    const makeVisual = (m: Extract<Project['media'][number], { kind: 'image' | 'video' }>) => {
      if (m.kind === 'image') {
        const img = document.createElement('img');
        img.src = m.src;
        img.alt = m.alt;
        img.loading = 'lazy';
        return img;
      }
      const video = document.createElement('video');
      video.src = m.src;
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.setAttribute('aria-label', m.alt);
      return video;
    };

    // Editorial rhythm: first visual is a full-width hero, then consecutive
    // visuals pair up side by side; quotes and unpaired visuals run full width.
    let i = 0;
    let isHero = true;
    while (i < p.media.length) {
      const m = p.media[i]!;
      if (m.kind === 'quote') {
        const q = document.createElement('p');
        q.className = 'media-quote';
        q.textContent = m.text;
        media.appendChild(q);
        i++;
        continue;
      }
      const next = p.media[i + 1];
      if (!isHero && next && next.kind !== 'quote') {
        const row = document.createElement('div');
        row.className = 'media-row';
        row.append(makeVisual(m), makeVisual(next));
        media.appendChild(row);
        i += 2;
      } else {
        media.appendChild(makeVisual(m));
        i++;
      }
      isHero = false;
    }

    overlay.hidden = false;
    overlay.scrollTop = 0;
    void startOverlayFx(overlay);
    document.body.classList.add('overlay-open');
    document.addEventListener('keydown', onKeydown);
    closeBtn.focus();
  };
}
